import { Lightbulb, Zap } from "lucide-react";
import { useNotify, type Identifier } from "ra-core";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { GulaClient } from "../gulaTypes";
import { SHIFTS, type ShiftKey } from "../shifts";
import { currentCallSlot, type Agent } from "../useAgents";
import {
  BATCH,
  OPEN_CAP,
  buildBuckets,
  pickNext,
  roomLeft,
  untouchedCalls,
  type Bucket,
  type BucketKey,
  type CallMission,
} from "./callPlan";
import { takeCalls } from "./useCallPlan";

/**
 * What to call next in each shift, by value. Work is taken a few leads at a
 * time and nobody holds more than OPEN_CAP leads they have not touched yet:
 * whoever works fast takes as many as they like, nobody sits on the pool.
 */
export const TakeWorkCard = ({
  title = "Recomendado ahora",
  onlyMe = false,
  clients,
  missions,
  agents,
  meId,
  onChanged,
}: {
  title?: string;
  /** In Mi jornada the work is always for the signed-in user */
  onlyMe?: boolean;
  clients: GulaClient[];
  missions: CallMission[];
  agents: Agent[];
  meId?: Identifier;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const [shift, setShift] = useState<ShiftKey>(
    () => (currentCallSlot() as ShiftKey | null) ?? "all",
  );
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const target = targetId || String(meId ?? "");
  const targetAgent = agents.find((agent) => String(agent.id) === target);
  const isMe = target === String(meId);

  const bucketsByShift = useMemo(() => {
    const claimedClientIds = new Set(
      missions.map((mission) => String(mission.client_id)),
    );
    return Object.fromEntries(
      SHIFTS.map((item) => [
        item.key,
        buildBuckets({
          clients,
          claimedClientIds,
          meId: target,
          shift: item.key,
        }),
      ]),
    ) as Record<ShiftKey, Bucket[]>;
  }, [clients, missions, target]);
  const buckets = bucketsByShift[shift];
  const totalOf = (key: ShiftKey) =>
    bucketsByShift[key].reduce((sum, bucket) => sum + bucket.clients.length, 0);

  const lastActivity = useMemo(
    () =>
      new Map(
        clients.map((client) => [String(client.id), client.last_activity_at]),
      ),
    [clients],
  );
  const untouched = untouchedCalls(missions, target, (clientId) =>
    lastActivity.get(String(clientId)),
  );
  const room = roomLeft(untouched);
  const nextCount = Math.min(BATCH, room, totalOf(shift));
  const who = isMe ? "Tú" : targetAgent?.first_name;

  const take = async (
    picks: { bucket: BucketKey; ids: GulaClient["id"][] }[],
    busyKey: string,
  ) => {
    setBusy(busyKey);
    try {
      let taken = 0;
      for (const pick of picks) {
        taken += await takeCalls(pick.ids, target, pick.bucket);
      }
      notify(
        taken
          ? `${taken} llamadas añadidas a la jornada de ${isMe ? "ti" : targetAgent?.first_name}`
          : "Alguien se te ha adelantado: ya estaban cogidas",
        { type: taken ? "success" : "info" },
      );
      onChanged();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="flex flex-col gap-4 rounded-3xl border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-lime-600" />
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <label
          hidden={onlyMe}
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          Para
          <select
            aria-label="Para quién"
            value={target}
            onChange={(event) => setTargetId(event.target.value)}
            className="rounded-full border bg-background px-2.5 py-1 text-sm text-foreground"
          >
            {agents
              .filter((agent) => !agent.no_work)
              .map((agent) => (
                <option key={agent.id} value={String(agent.id)}>
                  {String(agent.id) === String(meId)
                    ? "mí"
                    : `${agent.first_name} ${agent.last_name}`.trim()}
                </option>
              ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Turno">
        {SHIFTS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={shift === item.key}
            onClick={() => setShift(item.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-sm transition-colors",
              shift === item.key
                ? "border-neutral-900 bg-neutral-900 text-white dark:border-lime-300 dark:bg-lime-300 dark:text-neutral-900"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
            {item.key === currentCallSlot() ? " · ahora" : ""}
            <span className="ml-1.5 opacity-70">{totalOf(item.key)}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 rounded-2xl bg-muted/50 p-3">
        <span className="text-sm">
          {room
            ? `${who} ${isMe ? "tienes" : "tiene"} ${untouched} sin tocar · ${isMe ? "puedes" : "puede"} coger ${room} más. Sin límite: en cuanto llames, escribas o saltes uno, se abre hueco.`
            : `${who} ya ${isMe ? "tienes" : "tiene"} ${OPEN_CAP} sin tocar: llama, escribe por WhatsApp o salta alguno y ${isMe ? "coges" : "coge"} más al momento.`}
        </span>
        <Button
          className="w-fit rounded-full"
          disabled={!!busy || nextCount === 0}
          onClick={() => take(pickNext(buckets, nextCount), "next")}
        >
          <Zap className="h-4 w-4" />
          {nextCount > 1
            ? `Dame ${isMe ? "mis" : "sus"} ${nextCount} siguientes`
            : nextCount === 1
              ? "Dame el siguiente"
              : "Nada más que coger ahora"}
        </Button>
      </div>

      {buckets.length ? (
        <ul className="flex flex-col gap-2">
          {buckets.map((bucket, index) => (
            <li
              key={bucket.key}
              className={cn(
                "flex flex-wrap items-center gap-3 rounded-2xl border p-3",
                index === 0 && "border-lime-400 bg-lime-50 dark:bg-lime-950/30",
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold">
                {bucket.clients.length}
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="font-medium">
                  {bucket.title}
                  {index === 0 ? (
                    <span className="ml-2 rounded-full bg-lime-300 px-2 py-0.5 text-[11px] font-semibold text-neutral-900">
                      Prioridad
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-muted-foreground">
                  {bucket.why}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {bucket.clients
                    .slice(0, 3)
                    .map((client) => client.first_name)
                    .join(", ")}
                  {bucket.clients.length > 3 ? "…" : ""}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={!!busy || room === 0}
                onClick={() =>
                  take(
                    [
                      {
                        bucket: bucket.key,
                        ids: bucket.clients
                          .slice(0, Math.min(BATCH, room))
                          .map((client) => client.id),
                      },
                    ],
                    bucket.key,
                  )
                }
              >
                Coger {Math.min(BATCH, room, bucket.clients.length) || ""}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-2xl bg-muted/50 p-4 text-sm text-muted-foreground">
          No queda nada libre en este turno. Mira otro turno o «Todos».
        </p>
      )}
    </section>
  );
};
